import { useForm, getFormProps, getInputProps } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useTranslation } from 'react-i18next'
import {
  data,
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
  type MetaFunction,
  Form,
  Link,
  useActionData,
  useSearchParams,
} from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'

import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import {
  CheckboxField,
  ErrorList,
  Field,
  PasswordField,
} from '#app/components/forms.tsx'
import OnboardingShapes from '#app/components/onboarding-shapes.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { getInstance, getLocale } from '#app/middleware/i18next.ts'
import { signIn, requireAnonymous } from '#app/utils/auth.server.ts'
import { contactEmail, siteTitle } from '#app/utils/global.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { invariantResponse, useIsPending } from '#app/utils/misc.tsx'
import { isAuthorized } from '#app/utils/site-auth.server.ts'
import { EmailSchema, PasswordSchema } from '#app/utils/user-validation.ts'
import { handleNewSession } from './signin.server.ts'

const SignInFormSchema = z.object({
  email: EmailSchema,
  password: PasswordSchema,
  redirectTo: z.string().optional(),
  remember: z.boolean().optional(),
})

export async function loader({ context, request }: LoaderFunctionArgs) {
  invariantResponse(isAuthorized(request), 'Unauthorized', {
    status: 401,
  })

  const locale = getLocale(context)
  const i18next = getInstance(context)
  const t = await i18next.getFixedT(locale)

  const pageTitle = t('signIn.title')

  await requireAnonymous(request)
  return { pageTitle }
}

export async function action({ request }: ActionFunctionArgs) {
  await requireAnonymous(request)
  const formData = await request.formData()
  await checkHoneypot(formData)
  const submission = await parseWithZod(formData, {
    schema: intent =>
      SignInFormSchema.transform(async (data, ctx) => {
        if (intent !== null) return { ...data, session: null }

        const session = await signIn(data)
        if (!session) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'errors.signIn.invalidAccount',
          })
          return z.NEVER
        }

        return { ...data, session }
      }),
    async: true,
  })

  if (submission.status !== 'success' || !submission.value.session) {
    return data(
      { result: submission.reply({ hideFields: ['password'] }) },
      { status: submission.status === 'error' ? 400 : 200 },
    )
  }

  const { session, remember, redirectTo } = submission.value

  return handleNewSession({
    request,
    session,
    remember: remember ?? false,
    redirectTo: redirectTo ?? '/dashboard',
  })
}

export default function SignInPage() {
  const actionData = useActionData<typeof action>()
  const isPending = useIsPending()
  const [searchParams] = useSearchParams()
  const redirectTo = searchParams.get('redirectTo')
  const { t } = useTranslation()

  const [form, fields] = useForm({
    id: 'sign-in-form',
    constraint: getZodConstraint(SignInFormSchema),
    defaultValue: { redirectTo },
    lastResult: actionData?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: SignInFormSchema })
    },
    shouldRevalidate: 'onInput',
  })

  return (
    <main
      id="main-content"
      className="container flex flex-col items-center justify-center overflow-x-hidden pb-32"
    >
      <div className="relative lg:w-[460px]">
        <OnboardingShapes
          circleClassNames="top-0 w-[105px] lg:top-14 lg:w-[147px]"
          triangleClassNames="-right-7 top-[-0.9rem] w-[80px] lg:-right-32 lg::top-0 lg:w-[155px]"
        />
        <div className="relative">
          <div className="my-4 text-center lg:mt-24 lg:mb-6">
            <h1 className="font-poppins text-5 font-bold">
              {t('signIn.header')}
            </h1>
          </div>

          <Form method="POST" {...getFormProps(form)}>
            <HoneypotInputs />
            <Field
              labelProps={{ children: t('signIn.emailFieldLabel') }}
              inputProps={{
                ...getInputProps(fields.email, { type: 'email' }),
                autoFocus: true,
                autoComplete: 'email',
                placeholder: t('signIn.emailFieldPlaceholder'),
              }}
              errors={fields.email.errors}
            />

            <PasswordField
              labelProps={{ children: t('passwordFieldLabel') }}
              inputProps={{
                ...getInputProps(fields.password, {
                  type: 'password',
                }),
                placeholder: t('passwordFieldPlaceholder'),
              }}
              errors={fields.password.errors}
            />

            <div className="flex justify-between">
              <CheckboxField
                labelProps={{
                  htmlFor: fields.remember.id,
                  children: t('signIn.rememberMeLabel'),
                }}
                buttonProps={getInputProps(fields.remember, {
                  type: 'checkbox',
                })}
                errors={fields.remember.errors}
              />
              <div>
                <Link to="/forgot-password" className="text-1 text-cyan-11">
                  {t('signIn.forgotPasswordLink')}
                </Link>
              </div>
            </div>

            <input
              {...getInputProps(fields.redirectTo, { type: 'hidden' })}
              key="redirect-to-field"
            />
            <ErrorList errors={form.errors} id={form.errorId} />

            <div className="flex items-center justify-between gap-6 pt-3">
              <StatusButton
                status={isPending ? 'pending' : (form.status ?? 'idle')}
                type="submit"
                disabled={isPending}
              >
                {t('signIn.button')}
              </StatusButton>
            </div>
          </Form>
          <div className="border-mauve-6 mt-4 border-t border-solid py-4 text-center lg:mt-6 lg:py-6">
            <p className="text-2">
              {t('signIn.signUpCopy1')}
              <Link className="text-cyan-11" to="/signup">
                {t('signIn.signUpLink')}
              </Link>
              {t('signIn.signUpCopy2')}
            </p>
            <p className="text-2 mt-4">
              {t('signIn.needHelp')}{' '}
              <a className="text-cyan-11" href={`mailto:${contactEmail}`}>
                {t('signIn.contactUs')}
              </a>
              .
            </p>
          </div>
          <div className="border-mauve-6 border-t border-solid py-4 text-center lg:hidden">
            <p className="text-2">{t('signIn.deviceRecommendation')}</p>
          </div>
        </div>
      </div>
    </main>
  )
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const pageTitle = data?.pageTitle ?? 'Error'
  return [{ title: `${pageTitle} | ${siteTitle}` }]
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />
}
